/**
 * Composição do painel: cabeçalho, resumo, lista, mapa e detalhe.
 *
 * A tela não busca nada por conta própria — quem alimenta a store é o
 * `useFleetStream`, e quem mantém a sessão viva é o `useSessionKeeper`. Aqui só
 * se decide o que aparece.
 */
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { AppHeader } from "./components/AppHeader";
import { FleetMap } from "./components/FleetMap";
import { FleetSummary } from "./components/FleetSummary";
import { LoginScreen } from "./components/LoginScreen";

import { VehicleList } from "./components/VehicleList";
import { EmptyState } from "./components/ui";
import { api } from "./lib/api.ts";
import type { FleetIndicator } from "./lib/schemas.ts";
import { useFleetStream } from "./hooks/useFleetStream.ts";

/**
 * O detalhe carrega o `recharts`, que sozinho é a maior dependência do painel.
 * Sob demanda: quem só olha o mapa não paga por um gráfico que não abriu.
 */
/** O relatório carrega recharts e a lista de veículos; quem só olha o mapa não paga. */
/** Alertas e relatório carregam sob demanda, como o detalhe. */
const AlertsView = lazy(() => import("./components/AlertsView").then((m) => ({ default: m.AlertsView })));

const ConsumptionView = lazy(() =>
  import("./components/ConsumptionView").then((m) => ({ default: m.ConsumptionView })),
);
const LinksView = lazy(() => import("./components/LinksView").then((m) => ({ default: m.LinksView })));
/** Cadastros é um `lazy` só, com todas as abas dentro: quem opera o mapa não baixa nada disto. */
const RegistryView = lazy(() =>
  import("./components/registry/RegistryView").then((m) => ({ default: m.RegistryView })),
);
/** Operação (F10): fila de tratativas, comunicação e tipos de alerta. */
const OperationsView = lazy(() =>
  import("./components/operations/OperationsView").then((m) => ({ default: m.OperationsView })),
);

const VehicleDetailPanel = lazy(() =>
  import("./components/VehicleDetail").then((module) => ({ default: module.VehicleDetailPanel })),
);
import { useSessionKeeper } from "./hooks/useSessionKeeper.ts";
import { useAuthStore } from "./store/auth.store.ts";
import { useFleetStore } from "./store/fleet.store.ts";
import { useHistoryStore } from "./store/history.store.ts";
import { useMapStore } from "./store/map.store.ts";
import { useGeofenceStore } from "./store/geofence.store.ts";

export function App() {
  const token = useAuthStore((state) => state.token);
  useSessionKeeper();
  useFleetStream();

  if (!token) return <LoginScreen />;
  return <Dashboard />;
}

function Dashboard() {
  const logout = useAuthStore((state) => state.logout);
  const vehiclesMap = useFleetStore((state) => state.vehicles);
  const connection = useFleetStore((state) => state.connection);
  const updatedAt = useFleetStore((state) => state.updatedAt);
  const warning = useFleetStore((state) => state.warning);
  const recentlyChanged = useFleetStore((state) => state.recentlyChanged);
  const trails = useFleetStore((state) => state.trails);
  const history = useHistoryStore((state) => state.rows);
  const view = useMapStore((state) => state.view);
  const token = useAuthStore((state) => state.token);
  const loadGeofences = useGeofenceStore((state) => state.load);

  /**
   * Alerta pendente e motorista, por equipamento.
   *
   * De meio em meio minuto, e não junto do snapshot: alerta entra na fila
   * quando alguém trata, motorista é atribuído à mão. Pedir isso no ritmo das
   * posições seria duas consultas agregadas por segundo para responder a mesma
   * coisa. Falhar aqui não derruba nada — a lista só fica sem os dois ícones.
   */
  const [indicators, setIndicators] = useState<Map<string, FleetIndicator>>(new Map());
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    const load = () =>
      api
        .fleetIndicators(token, controller.signal)
        .then((data) => setIndicators(new Map(data.vehicles.map((v) => [v.imei, v]))))
        .catch(() => {});
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [token]);

  // Uma vez, na subida: cerca muda por cadastro, não a cada segundo.
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    void loadGeofences(token, controller.signal);
    return () => controller.abort();
  }, [token, loadGeofences]);
  const selectedImei = useFleetStore((state) => state.selectedImei);
  const search = useFleetStore((state) => state.search);
  const statusFilter = useFleetStore((state) => state.statusFilter);
  const select = useFleetStore((state) => state.select);
  const detailOpen = useFleetStore((state) => state.detailOpen);
  const closeDetail = useFleetStore((state) => state.closeDetail);
  const setSearch = useFleetStore((state) => state.setSearch);
  const setStatusFilter = useFleetStore((state) => state.setStatusFilter);

  // A store guarda um Map (atualização por IMEI é O(1)); a tela quer lista.
  const vehicles = useMemo(() => [...vehiclesMap.values()], [vehiclesMap]);
  const selected = selectedImei ? (vehiclesMap.get(selectedImei) ?? null) : null;

  return (
    <div className="flex h-full flex-col bg-app">
      <AppHeader connection={connection} updatedAt={updatedAt} onLogout={logout} />

      {view === "alerts" ? (
        <Suspense fallback={<div className="p-8 text-center text-xs text-muted">carregando alertas…</div>}>
          <AlertsView />
        </Suspense>
      ) : view === "links" ? (
        <Suspense fallback={<div className="p-8 text-center text-xs text-muted">carregando vínculos…</div>}>
          <LinksView />
        </Suspense>
      ) : view === "operations" ? (
        <Suspense fallback={<div className="p-8 text-center text-xs text-muted">carregando operação…</div>}>
          <OperationsView />
        </Suspense>
      ) : view === "registry" ? (
        <Suspense fallback={<div className="p-8 text-center text-xs text-muted">carregando cadastros…</div>}>
          <RegistryView />
        </Suspense>
      ) : view === "consumption" ? (
        <Suspense fallback={<div className="p-8 text-center text-xs text-muted">carregando consumo…</div>}>
          <ConsumptionView />
        </Suspense>
      ) : (
      <>
      <div className="border-b border-line px-4 py-3">
        <FleetSummary vehicles={vehicles} active={statusFilter} onSelect={setStatusFilter} />
      </div>

      {warning ? (
        <p className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs text-amber-200">
          {warning} — o mapa pode estar incompleto.
        </p>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="hidden w-80 shrink-0 border-r border-line md:block">
          <VehicleList
            vehicles={vehicles}
            search={search}
            statusFilter={statusFilter}
            selectedImei={selectedImei}
            recentlyChanged={recentlyChanged}
            indicators={indicators}
            onSearch={setSearch}
            onSelect={select}
          />
        </div>

        <main className="relative min-w-0 flex-1">
          {vehicles.length === 0 ? (
            <EmptyState
              title={connection === "live" ? "Nenhum veículo no snapshot" : "Conectando ao fluxo ao vivo…"}
              hint="A frota aparece assim que o primeiro pacote for processado."
            />
          ) : (
            <FleetMap
              vehicles={vehicles}
              selected={selected}
              selectedTrail={(selectedImei ? trails.get(selectedImei) : undefined) ?? []}
              selectedHistory={history}
              onSelect={select}
            />
          )}
        </main>

        {/* Fechar a gaveta não desseleciona: o veículo continua destacado no
            mapa, com o rastro. Quem quer largá-lo usa o botão de desselecionar,
            no canto do mapa. */}
        <div className={`shrink-0 ${selected && detailOpen ? "w-full max-w-md" : "w-0"} transition-[width] duration-200`}>
          <Suspense fallback={null}>
            <VehicleDetailPanel vehicle={detailOpen ? selected : null} onClose={closeDetail} />
          </Suspense>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
